import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MapPin, Calendar, Users, ChevronRight, Heart, ChevronDown, RefreshCw, Tag, Percent } from 'lucide-react';
import ImageWithLoader from './ImageWithLoader';
import { experiencesData } from '../data/experiencesData';
import PriceDisplay from './PriceDisplay';

// Lazy load the BookingModal component
const BookingModal = lazy(() => import('./BookingModal'));

const PopularExperiences = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [showAll, setShowAll] = useState(false);
  const [selectedExperience, setSelectedExperience] = useState<typeof experiencesData[0] | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [loadedImages, setLoadedImages] = useState<number[]>([]);
  const initialDisplayCount = 9;
  const { t } = useTranslation();

  // Filter experiences based on selected category
  const filteredExperiences = selectedCategory === 'All' 
    ? experiencesData 
    : experiencesData.filter(exp => exp.category === selectedCategory);
  
  // Determine how many experiences to display
  const displayedExperiences = showAll 
    ? filteredExperiences 
    : filteredExperiences.slice(0, initialDisplayCount);

  // Calculate discounted price (3% off)
  const calculateDiscountedPrice = (originalPrice: number) => {
    const discountedPrice = originalPrice * 0.97; // 3% off
    return discountedPrice.toFixed(0); // Round to whole number
  };

  const handleBookClick = (experience: typeof experiencesData[0]) => {
    // Apply the discount to the experience before passing it to the modal
    const discountedExperience = {
      ...experience,
      originalPrice: experience.price,
      price: parseInt(calculateDiscountedPrice(experience.price))
    };
    setSelectedExperience(discountedExperience);
    setIsModalOpen(true);
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    setLoadedImages([]);
    
    // Simulate refreshing data
    setTimeout(() => {
      setIsRefreshing(false);
    }, 1000);
  };

  const handleImageLoad = (id: number) => {
    setLoadedImages(prev => [...prev, id]);
  };

  const categories = [
    'All', 'Wildlife', 'Safari', 'Cultural', 'Adventure', 'Birding'
  ];

  return (
    <section className="py-12 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{t('experiences.title')}</h2>
            <p className="mt-1 text-gray-600">{t('experiences.subtitle')}</p>
          </div>
          <div className="flex items-center gap-2">
            <button 
              onClick={handleRefresh}
              className="p-2 rounded-full bg-white shadow-sm hover:shadow-md transition-all duration-300 focus:outline-none"
              aria-label="Refresh experiences"
            >
              <RefreshCw className={`h-4 w-4 text-ta-green ${isRefreshing ? 'animate-spin' : ''}`} />
            </button>
            <Link to="/all-experiences" className="hidden md:flex items-center text-ta-green hover:underline">
              {t('common.seeAll')}
              <ChevronRight className="ml-1 h-4 w-4" />
            </Link>
          </div>
        </div>
        
        <div className="flex overflow-x-auto pb-4 mb-6 scrollbar-hide">
          <div className="flex space-x-2">
            {categories.map((category, index) => (
              <button
                key={index}
                className={`px-4 py-2 rounded-full whitespace-nowrap transition-all duration-300 ${
                  category === selectedCategory 
                    ? 'bg-ta-green text-white shadow-md' 
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
                onClick={() => {
                  setSelectedCategory(category);
                  setShowAll(false);
                }}
              >
                {t(`experiences.categories.${category.toLowerCase()}`)}
              </button>
            ))}
          </div>
        </div>
        
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 sm:gap-4">
          {displayedExperiences.map((experience) => (
            <div 
              key={experience.id} 
              className={`bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-md transition-all duration-300 ${
                isRefreshing ? 'opacity-50 animate-pulse' : 'opacity-100'
              }`}
            >
              <div className="relative h-32">
                <ImageWithLoader 
                  src={experience.image} 
                  alt={experience.title} 
                  className="w-full h-full object-cover"
                />
                <button className="absolute top-2 right-2 bg-white/80 p-1 rounded-full hover:bg-white transition-colors">
                  <Heart className="h-4 w-4 text-gray-600 hover:text-red-500" />
                </button>
                <div className="absolute bottom-2 left-2 bg-ta-green text-white text-[8px] sm:text-xs px-1.5 py-0.5 rounded-sm">
                  {experience.category}
                </div>
                {/* Discount Tag */}
                <div className="absolute top-2 left-2 bg-red-500 text-white text-[8px] sm:text-xs px-1.5 py-0.5 rounded-sm flex items-center">
                  <Percent className="h-2 w-2 sm:h-3 sm:w-3 mr-0.5" />
                  -3%
                </div>
              </div>
              <div className="p-2">
                <h3 className="text-[10px] sm:text-xs font-semibold text-gray-900 mb-0.5 line-clamp-1">{experience.title}</h3>
                <div className="flex items-center text-gray-600 mb-0.5">
                  <MapPin className="h-2 w-2 sm:h-3 sm:w-3 text-ta-green-dark" />
                  <span className="ml-0.5 text-[8px] sm:text-[10px] truncate">{experience.location}</span>
                </div>
                <div className="flex flex-wrap gap-1 text-[8px] sm:text-[10px] text-gray-600 mb-1.5">
                  <div className="flex items-center">
                    <Calendar className="h-2 w-2 sm:h-3 sm:w-3 text-ta-green-dark" />
                    <span className="ml-0.5 truncate">{experience.duration}</span>
                  </div>
                  <div className="flex items-center">
                    <Users className="h-2 w-2 sm:h-3 sm:w-3 text-ta-green-dark" />
                    <span className="ml-0.5 truncate">{experience.groupSize}</span>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-[6px] sm:text-[8px] text-gray-500">{t('common.from')}</p>
                    <div className="flex items-center">
                      <PriceDisplay 
                        price={parseInt(calculateDiscountedPrice(experience.price))} 
                        size="sm" 
                        className="text-gray-900 font-bold" 
                      />
                      <p className="text-[6px] sm:text-[8px] text-gray-500 line-through ml-0.5">
                        <PriceDisplay price={experience.price} size="sm" className="text-gray-500" />
                      </p>
                    </div>
                  </div>
                  <div className="flex space-x-1">
                    <Link 
                      to={`/experiences/${experience.id}`}
                      className="py-0.5 px-1 sm:px-1.5 text-[6px] sm:text-[8px] bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                    >
                      {t('experiences.viewDetails')}
                    </Link>
                    <button 
                      className="py-0.5 px-1 sm:px-1.5 text-[6px] sm:text-[8px] bg-ta-green text-white rounded hover:bg-ta-green-dark transition-colors"
                      onClick={() => handleBookClick(experience)}
                    >
                      {t('common.book')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {filteredExperiences.length > initialDisplayCount && (
          <div className="mt-8 text-center">
            <button 
              onClick={() => setShowAll(!showAll)}
              className="px-6 py-2 border border-ta-green text-ta-green rounded-md hover:bg-ta-green/5 transition-colors flex items-center mx-auto"
            >
              {showAll ? t('common.viewLess') : t('common.viewMore')}
              <ChevronDown className={`ml-1 h-4 w-4 transition-transform ${showAll ? 'rotate-180' : ''}`} />
            </button>
          </div>
        )}
        
        <div className="mt-6 text-center md:hidden">
          <Link to="/all-experiences" className="inline-flex items-center text-ta-green hover:underline">
            {t('common.seeAll')}
            <ChevronRight className="ml-1 h-4 w-4" />
          </Link>
        </div>
      </div>
      
      {/* Booking Modal */}
      {selectedExperience && isModalOpen && (
        <Suspense fallback={<div className="fixed inset-0 bg-black/60 flex items-center justify-center">Loading...</div>}>
          <BookingModal 
            experience={selectedExperience}
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
          />
        </Suspense>
      )}
    </section>
  );
};

export default PopularExperiences;